import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  X,
  Clock,
  Trash2,
  Save,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Loader2,
  Plus,
  Search,
} from 'lucide-react';
import { formatTime, getTypeConfig, getParkDisplayName, PARK_NAMES } from './helpers';
import { toast } from '../../../../../utils/toast';

// Park key → API park name mapping for enhanced itinerary endpoints
const PARK_KEY_TO_API_NAME = {
  MK: 'Magic Kingdom',
  DHS: 'Hollywood Studios',
  EPCOT: 'EPCOT',
  AK: 'Animal Kingdom',
};

/**
 * AddActivityPanel — inline panel for adding new items to a day.
 * Fetches rides/shows/dining from backend APIs based on park.
 */
function AddActivityPanel({ parkKey, onAdd, onCancel }) {
  const [activityType, setActivityType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [timeValue, setTimeValue] = useState('12:00');
  const [customName, setCustomName] = useState('');

  const parkName = PARK_KEY_TO_API_NAME[parkKey] || parkKey;
  const isCustomType = activityType === 'buffer' || activityType === 'note';

  // Fetch options when type is selected (rides, shows, dining)
  useEffect(() => {
    if (!activityType || isCustomType) { setOptions([]); return; }

    const fetchOptions = async () => {
      setLoadingOptions(true);
      setOptions([]);
      try {
        let endpoint = '';
        if (activityType === 'ride') {
          endpoint = `${import.meta.env.VITE_API_URL}/api/magicflow/attractions?park=${encodeURIComponent(parkName)}`;
        } else if (activityType === 'show') {
          endpoint = `${import.meta.env.VITE_API_URL}/api/enhanced-itinerary/shows/${encodeURIComponent(parkName)}`;
        } else if (activityType === 'dining') {
          endpoint = `${import.meta.env.VITE_API_URL}/api/enhanced-itinerary/restaurants/${encodeURIComponent(parkName)}`;
        }
        if (!endpoint) return;

        const res = await fetch(endpoint, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setOptions(list.map(item => ({
          name: item.name || item.Name || item.displayName || 'Unknown',
          zone: item.area || item.zone || item.Zone || item.location || '',
          type: activityType,
        })));
      } catch {
        toast.error(`Failed to load ${activityType} options`);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [activityType, parkName, isCustomType]);

  const filteredOptions = searchQuery.length >= 1
    ? options.filter(o => o.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  const handleAdd = () => {
    const [h, m] = timeValue.split(':').map(Number);
    const startMin = (h || 0) * 60 + (m || 0);
    const name = isCustomType ? customName.trim() : selectedOption?.name;
    if (!name) { toast.error('Please select or enter an activity'); return; }

    onAdd({
      name,
      type: activityType === 'note' ? 'open_time' : activityType,
      zone: selectedOption?.zone || '',
      startMin,
      endMin: startMin + 30, // Default 30 min duration
      durationMin: 30,
      isTransfer: false,
      meta: { addedByAgent: true },
    });
  };

  const TYPES = [
    { value: 'ride', label: 'Ride' },
    { value: 'show', label: 'Show' },
    { value: 'dining', label: 'Dining' },
    { value: 'buffer', label: 'Break' },
    { value: 'note', label: 'Note / Free Time' },
  ];

  return (
    <div className="p-4 mt-3 rounded-lg border border-blue-200 bg-blue-50/50">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-sm font-medium text-gray-900">Add Activity</h4>
        <button type="button" onClick={onCancel} className="p-1 text-gray-400 rounded hover:text-gray-600 hover:bg-gray-100">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Type selector */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {TYPES.map(t => (
          <button
            key={t.value}
            type="button"
            onClick={() => { setActivityType(t.value); setSelectedOption(null); setSearchQuery(''); setCustomName(''); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              activityType === t.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activityType && (
        <div className="space-y-3">
          {/* Custom name input for break/note */}
          {isCustomType ? (
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder={activityType === 'buffer' ? 'e.g., Afternoon break' : 'e.g., Explore Fantasyland'}
              className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : (
            <>
              {/* Search input for API-backed types */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 w-3.5 h-3.5 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${activityType}s at ${getParkDisplayName(parkKey)}...`}
                  className="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Options list */}
              <div className="overflow-y-auto max-h-40 rounded-lg border border-gray-200 bg-white">
                {loadingOptions ? (
                  <div className="flex gap-2 justify-center items-center py-4 text-xs text-gray-400">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                  </div>
                ) : filteredOptions.length === 0 ? (
                  <div className="py-4 text-xs text-center text-gray-400">
                    {options.length === 0 ? `No ${activityType}s available` : 'No matches'}
                  </div>
                ) : (
                  filteredOptions.map((opt, i) => (
                    <button
                      key={`${opt.name}-${i}`}
                      type="button"
                      onClick={() => setSelectedOption(opt)}
                      className={`flex justify-between items-center px-3 py-2 w-full text-left text-sm transition-colors hover:bg-blue-50 ${
                        selectedOption?.name === opt.name ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="truncate">{opt.name}</span>
                      {opt.zone && <span className="flex-shrink-0 ml-2 text-xs text-gray-400">{opt.zone}</span>}
                    </button>
                  ))
                )}
              </div>
            </>
          )}

          {/* Time picker + Add button */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block mb-1 text-xs font-medium text-gray-600">Time</label>
              <input
                type="time"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleAdd}
              disabled={isCustomType ? !customName.trim() : !selectedOption}
              className="flex gap-1.5 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Edit Day Modal for V3 itineraries.
 *
 * Provides a list-based editor for reordering, removing, and editing times
 * of items in a day. Uses move-up/move-down for accessibility (no drag-drop).
 */
function EditDayModal({ day, tripId, isOpen, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [editingTimeIdx, setEditingTimeIdx] = useState(null);
  const [timeInputValue, setTimeInputValue] = useState('');
  const [modifiedIndices, setModifiedIndices] = useState(new Set());
  const [showAddPanel, setShowAddPanel] = useState(false);

  // Initialize from day.items
  useEffect(() => {
    if (day?.items) {
      setItems(day.items.map((item, i) => ({ ...item, _key: `${i}-${item.name}` })));
      setModifiedIndices(new Set());
    }
  }, [day]);

  const hasChanges = useCallback(() => {
    if (!day?.items) return false;
    if (items.length !== day.items.length) return true;
    return items.some((item, i) => {
      const orig = day.items[i];
      return item.name !== orig.name || item.startMin !== orig.startMin;
    });
  }, [items, day]);

  // Detect time overlaps between adjacent items
  const timeOverlaps = useMemo(() => {
    const overlaps = new Set();
    for (let i = 1; i < items.length; i++) {
      const prev = items[i - 1];
      const curr = items[i];
      if (prev.endMin != null && curr.startMin != null && curr.startMin < prev.endMin) {
        overlaps.add(i);
        overlaps.add(i - 1);
      }
    }
    return overlaps;
  }, [items]);

  // Move item up/down
  const moveItem = (fromIdx, direction) => {
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= items.length) return;
    const next = [...items];
    [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
    setItems(next);
    setModifiedIndices((prev) => new Set([...prev, fromIdx, toIdx]));
  };

  // Remove item
  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setConfirmRemove(null);
    setModifiedIndices((prev) => {
      const next = new Set(prev);
      next.delete(idx);
      return next;
    });
  };

  // Time editing
  const startEditingTime = (idx) => {
    const item = items[idx];
    const h = Math.floor((item.startMin || 0) / 60);
    const m = (item.startMin || 0) % 60;
    setTimeInputValue(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    setEditingTimeIdx(idx);
  };

  const saveTimeEdit = () => {
    if (editingTimeIdx == null) return;
    const [hStr, mStr] = timeInputValue.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (isNaN(h) || isNaN(m)) {
      setEditingTimeIdx(null);
      return;
    }
    const newStartMin = h * 60 + m;
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== editingTimeIdx) return item;
        const duration = item.durationMin || (item.endMin ? item.endMin - item.startMin : 0);
        return {
          ...item,
          startMin: newStartMin,
          endMin: duration > 0 ? newStartMin + duration : item.endMin,
        };
      }),
    );
    setModifiedIndices((prev) => new Set([...prev, editingTimeIdx]));
    setEditingTimeIdx(null);
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/itinerary/${tripId}/update`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dayIndex: day.dayIndex,
            updatedDay: { ...day, items },
            originalDay: day,
            changeType: 'manual_edit',
            changeDescription: `Agent edited Day ${day.dayIndex + 1} schedule`,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save changes');
      }
      toast.success('Day updated successfully');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !day) return null;

  const parkName = getParkDisplayName(day.parkKey);
  const dayLabel = `Day ${day.dayIndex + 1} - ${parkName}`;
  const changed = hasChanges();

  return (
    <div
      className="flex fixed inset-0 z-modal justify-center items-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex flex-col w-full max-w-2xl max-h-[85vh] bg-white rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit {dayLabel}</h2>
            <p className="text-xs text-gray-500">
              Reorder activities, adjust times, or remove items
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Time overlap warning */}
        {timeOverlaps.size > 0 && (
          <div className="flex gap-2 items-center px-6 py-2 text-xs bg-amber-50 border-b border-amber-200 text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Some activities have overlapping times. Review the highlighted items before saving.</span>
          </div>
        )}

        {/* Item list */}
        <div className="overflow-y-auto flex-1 px-6 py-4 min-h-0">
          {items.length === 0 ? (
            <p className="py-8 text-sm text-center text-gray-400">
              No activities in this day.
            </p>
          ) : (
            <div className="space-y-1.5">
              {items.map((item, idx) => {
                const cfg = getTypeConfig(item.type);
                const isStructural = item.type === 'buffer' || item.type === 'transfer';
                const isModified = modifiedIndices.has(idx);
                const hasOverlap = timeOverlaps.has(idx);

                return (
                  <div
                    key={item._key || idx}
                    className={`flex gap-2 items-center px-3 py-2.5 rounded-lg border transition-colors ${
                      hasOverlap
                        ? 'border-amber-300 bg-amber-50/50'
                        : isModified
                        ? 'border-l-[3px] border-l-blue-500 border-t border-r border-b border-gray-200 bg-blue-50/30'
                        : isStructural
                        ? 'border-gray-100 bg-gray-50/50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {/* Move buttons — 44px touch targets */}
                    <div className="flex flex-col flex-shrink-0 gap-0.5">
                      <button
                        type="button"
                        onClick={() => moveItem(idx, -1)}
                        disabled={idx === 0}
                        className="flex justify-center items-center w-7 h-7 text-gray-400 rounded transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:hover:bg-transparent"
                        title="Move up"
                        style={{ minWidth: 28, minHeight: 28 }}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(idx, 1)}
                        disabled={idx === items.length - 1}
                        className="flex justify-center items-center w-7 h-7 text-gray-400 rounded transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-20 disabled:hover:bg-transparent"
                        title="Move down"
                        style={{ minWidth: 28, minHeight: 28 }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Time */}
                    <div className="flex-shrink-0 w-16">
                      {editingTimeIdx === idx ? (
                        <input
                          type="time"
                          value={timeInputValue}
                          onChange={(e) => setTimeInputValue(e.target.value)}
                          onBlur={saveTimeEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveTimeEdit();
                            if (e.key === 'Escape') setEditingTimeIdx(null);
                          }}
                          className="w-full px-1 py-0.5 text-xs rounded border border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEditingTime(idx)}
                          className={`flex gap-1 items-center text-xs transition-colors hover:text-blue-600 ${
                            hasOverlap ? 'text-amber-600 font-medium' : 'text-gray-500'
                          }`}
                          title="Click to edit time"
                        >
                          <Clock className="w-3 h-3" />
                          {item.startMin != null ? formatTime(item.startMin) : '--:--'}
                        </button>
                      )}
                    </div>

                    {/* Name + type */}
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${isStructural ? 'text-gray-400' : 'text-gray-900'}`}>
                        {item.name}
                      </span>
                    </div>

                    {/* Type badge */}
                    <span
                      className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full border ${cfg.style}`}
                    >
                      {cfg.label}
                    </span>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => setConfirmRemove(idx)}
                      className="flex justify-center items-center flex-shrink-0 w-7 h-7 text-gray-300 rounded transition-colors hover:text-red-500 hover:bg-red-50"
                      title="Remove"
                      style={{ minWidth: 28, minHeight: 28 }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Remove confirmation */}
          {confirmRemove != null && items[confirmRemove] && (
            <div className="flex gap-3 justify-between items-center p-3 mt-3 rounded-lg border border-red-200 bg-red-50">
              <div className="flex gap-2 items-center text-sm text-red-700">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Remove <strong>{items[confirmRemove].name}</strong>?
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRemove(null)}
                  className="px-3 py-1 text-xs font-medium text-gray-600 bg-white rounded border border-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(confirmRemove)}
                  className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded transition-colors hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          {/* Add Activity */}
          {showAddPanel ? (
            <AddActivityPanel
              parkKey={day.parkKey}
              onAdd={(newItem) => {
                const key = `added-${items.length}-${newItem.name}`;
                setItems((prev) => {
                  // Insert sorted by startMin
                  const updated = [...prev, { ...newItem, _key: key }];
                  updated.sort((a, b) => (a.startMin ?? 9999) - (b.startMin ?? 9999));
                  return updated;
                });
                setModifiedIndices((prev) => new Set([...prev, items.length]));
                setShowAddPanel(false);
              }}
              onCancel={() => setShowAddPanel(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowAddPanel(true)}
              className="flex gap-1.5 items-center px-3 py-2 mt-3 w-full text-sm font-medium text-blue-600 rounded-lg border border-dashed border-blue-300 transition-colors hover:bg-blue-50"
            >
              <Plus className="w-4 h-4" />
              Add Activity
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-between items-center px-6 py-4 border-t border-gray-200">
          <div className="text-xs text-gray-400">
            {changed
              ? `${modifiedIndices.size || ''} item${modifiedIndices.size !== 1 ? 's' : ''} modified`
              : ''}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <div className="relative group">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !changed}
                className="flex gap-1.5 items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {/* Tooltip on disabled save */}
              {!changed && !saving && (
                <div className="absolute right-0 bottom-full mb-2 px-2 py-1 text-[10px] text-white bg-gray-800 rounded opacity-0 transition-opacity pointer-events-none group-hover:opacity-100 whitespace-nowrap">
                  No changes to save
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(EditDayModal);
